/**
 * Live wall-drag preview slice (shape handles).
 *
 * Endpoint/top handle drags need the wall body to follow the cursor
 * frame-by-frame. Pushing those frames through the BIM kernel would (a) push
 * an undo command per move, (b) run ConstraintSolver per move, and (c) replace
 * the project elements Map per move — re-rendering every subscriber of the
 * map (instance batching, opening indexes) for a one-element change.
 *
 * Instead the drag publishes the in-progress geometryData here (a two-field
 * store). WallRenderer prefers it for the dragged wall ONLY — no other
 * component subscribes with a matching id, so nothing else re-renders per
 * drag frame. On release the final target commits ONCE through the kernel
 * (single undo step + exact join re-solve) and this slice is cleared.
 * Preview state is pure render input; it never touches history.
 */
import { create } from 'zustand';
import { WallGeometryData } from '../core/types';

interface WallEditPreviewState {
    /** Wall the drag preview is for (null when no drag runs). */
    wallId: string | null;
    /** In-progress geometry the wall should render instead of its committed data. */
    geometryData: WallGeometryData | null;
    /** Publish the in-progress geometry for one wall (null clears). */
    set: (wallId: string | null, geometryData: WallGeometryData | null) => void;
    clear: () => void;
}

export const useWallEditPreviewStore = create<WallEditPreviewState>((set) => ({
    wallId: null,
    geometryData: null,
    set: (wallId, geometryData) => set({ wallId, geometryData }),
    clear: () => set({ wallId: null, geometryData: null }),
}));
